import {
  EmployerPageNumber,
  HealthcareContactsPageNumber,
  PatientPageNumber,
  PaymentPageNumber,
  SectionNumber,
} from '~/components/Paperwork/constants';
import { PaperworkCopyFormFieldsCheckboxName } from '~/components/Paperwork/paperworkConstants';
import {
  ADD_NEW_INSURANCE,
  SELECT_EXISTING,
} from '~/components/Paperwork/components/Payment/PaymentInfoForm';

export default {
  common: {
    navTitle: 'Complete paperwork',
    patientInfo: 'Patient information',
    guardianInformation: 'Guardian information',
    contactInformation: 'Contact information',
    employerInformation: 'Employer information',
    insuranceInformation: 'Insurance information',
    primaryInsurance: 'Primary insurance',
    secondaryInsurance: 'Secondary insurance',
    emergencyContact: 'Emergency contact',
    additionalInformation: 'Additional information',
    consentForms: 'Consent forms',
    photoId: 'Photo ID',
    required: '{{FORM_FIELD_NAME}} is required',
    requiredShort: 'Required',
    optional: 'Optional',
    sectionSubtitle: 'Section {{CURRENT_SECTION}} of {{TOTAL_SECTIONS}}',
  },
  checkboxes: {
    [PaperworkCopyFormFieldsCheckboxName.ADDRESS]: 'Apply the same address as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.GUARDIAN_ADDRESS]:
      'Apply {{name}}’s address as Guardian address',
    [PaperworkCopyFormFieldsCheckboxName.PHOTO_ID]: 'Apply the same ID as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.INSURANCE]: 'Apply the same insurance as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.PHYSICIAN]: 'Apply the same physician as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.PHARMACY]: 'Apply the same pharmacy as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.EMERGENCY_CONTACT]:
      'Apply the same emergency contact as {{name}}',
    [PaperworkCopyFormFieldsCheckboxName.GUARANTOR_INFO]: 'Apply {{name}}’s guardian as guarantor',
  },
  [SectionNumber.Patient]: {
    sectionTitle: 'Identity & background',
    pages: {
      [PatientPageNumber.BasicInfo]: {
        genericTitle: '{{name}}’s information',
        personalTitle: '{{name}}’s information',
        subtitle:
          'Please use legal name to help the office keep track of your medical records more easily.',
      },
      [PatientPageNumber.Address]: {
        genericTitle: '{{name}}’s primary address',
        personalTitle: 'What is {{name}}’s primary address?',
        subtitle: '',
      },
      [PatientPageNumber.GuardianInfo]: {
        genericTitle: '',
        personalTitle: 'Who is {{name}}’s legal guardian?',
        subtitle: '',
      },
    },
  },
  [SectionNumber.Employer]: {
    sectionTitle: 'Employer information',
    pages: {
      [EmployerPageNumber.PageOne]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
      [EmployerPageNumber.EmployerFormFieldNames]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
      [EmployerPageNumber.CustomEmployerFormFieldNames]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
      [EmployerPageNumber.WorkRelatedInjuryFields]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
      [EmployerPageNumber.DrugScreenAlcoholTestFields]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
      [EmployerPageNumber.PhysicalFields]: {
        genericTitle: '{{name}}’s employer information',
        personalTitle: '',
        subtitle: '',
      },
    },
  },
  [SectionNumber.HealthcareContacts]: {
    sectionTitle: 'Healthcare contacts',
    pages: {
      [HealthcareContactsPageNumber.PageOne]: {
        genericTitle: '{{name}}’s healthcare contacts',
        personalTitle: '{{name}}’s healthcare contacts',
        subtitle: '',
      },
      [HealthcareContactsPageNumber.EmergencyContact]: {
        genericTitle: 'Who is your emergency contact?',
        personalTitle: 'Who is {{name}}’s emergency contact?',
        subtitle: '',
      },
    },
  },
  [SectionNumber.Payment]: {
    sectionTitle: 'Insurance information',
    pages: {
      [PaymentPageNumber.SelfPayCheck]: {
        genericTitle: 'Is {{name}} paying with insurance?',
        personalTitle: 'Is {{name}} paying with insurance?',
        subtitle: '',
      },
      [PaymentPageNumber.InsuranceInfo]: {
        genericTitle: '{{name}}’s insurance information',
        personalTitle: '{{name}}’s insurance information',
        subtitle: '',
      },
      [PaymentPageNumber.SecondaryInsurance]: {
        genericTitle: '{{name}}’s secondary insurance information',
        personalTitle: '{{name}}’s secondary insurance information',
        subtitle: '',
      },
      [PaymentPageNumber.GuarantorInfo]: {
        genericTitle: '{{name}}’s guarantor',
        personalTitle: '{{name}}’s guarantor',
        subtitle: `A guarantor is the individual responsible for the visit's financial obligations. The guarantor is typically always defined as the patient unless they are an unemancipated minor or an incapacitated adult. The guarantor is not defined as the insurance policy holder or head of household, although a guarantor may be an insurance policy holder. If you are not your own guarantor, then the guarantor must be the patient's legal guardian or legal spouse.`,
      },
    },
  },
  [SectionNumber.Custom]: {
    sectionTitle: 'Additional questions',
    genericTitle: 'Additional information for {{name}}',
    personalTitle: 'Additional information for {{name}}',
    subtitle: '',
  },
  [SectionNumber.VerificationAndReview]: {
    sectionTitle: 'Verification & review',
    genericTitle: '',
    personalTitle: '',
    subtitle: '',
  },
  [SectionNumber.Consent]: {
    sectionTitle: 'Verification & review',
    genericTitle: '',
    personalTitle: '',
    subtitle: '',
  },
  [SectionNumber.Finalize]: {
    sectionTitle: 'Verification & review',
    genericTitle: 'Verification & review',
    subtitle:
      'Review and confirm all the details below. If everything looks good, we’ll send to the provider.',
  },
  payment: {
    [SELECT_EXISTING]: 'Use my saved insurance',
    [`${SELECT_EXISTING}Subtitle`]: 'Log in to use your previously saved insurance information',
    [ADD_NEW_INSURANCE]: 'Add new insurance',
    chooseInsuranceMethodSubTitle: 'How would you like to add your insurance?',
    addNewInsuranceMethodSubTitle: 'Add new insurance',
    selectExistingInsuranceMethodSubTitle: 'Select an existing insurance or add a new card',
    noInsuranceFoundSubTitle: "Looks like we don't have any insurance cards saved for you",
    noInsuranceFound: {
      title: "Looks like we don't have any insurance cards saved for you",
      body: "Let's add a new insurance profile to your account.",
      promptTitle: 'No insurance on file',
      promptBody: "Let's add your insurance, we'll save your information for future use.",
    },
    confirmYourInsurance: 'Confirm your insurance information',
    seeMoreProfiles: 'Show {{profileNumber}} more insurance profiles',
    hideMoreProfiles: 'Hide',
    photoUpload: 'Photo upload',
    optional: 'Optional',
  },
  complete: {
    returnToAccount: 'Return to account',
    noThanks: 'No thanks, not right now',
    viewWaitlist: 'View live waitlist',
    viewVisitDetails: 'View visit details',
    paperworkReceived: 'The clinic has received your paperwork.',
    paperworkReceivedPersonal: 'The clinic has received {{name}}’s paperwork',
    continueCompletingPaperwork:
      'Would you like to continue completing paperwork for the rest of your family?',
    returnTablet: 'Please return the tablet to the front desk',
    allSet: "You're all set!",
    edit: 'Edit paperwork',
    visitDetails: 'View visit details',
    upsell: {
      title: 'Maximize your health benefits',
      trackSpending: 'Track your spending, see hidden perks',
      costEstimates: 'and get cost estimates for your care.',
      viewMyBenefits: 'View my benefits',
    },
    intellivisit: {
      almostDone: 'Almost done!',
      completeQuestionnaire: 'Complete your pre-exam questionnaire',
      pleaseComplete:
        'To save time at your visit and personalize your care, please complete the pre-exam medical questionnaire to tell your provider more about the symptoms you are experiencing.',
    },
  },
  consent: {
    title: 'Review and sign forms',
    subtitle: 'Please review the attached documents and add your digital signature below.',
    addSignature: 'Tap to add signature',
    agreementCopy:
      'By continuing, I understand and agree to the terms and conditions contained in the forms above and agree to sign them electronically.',
    htmlTitle: 'Paperwork – consent',
    error: {
      message: 'Please see the front desk to sign your forms.',
    },
    updateToView: 'Please update your app to view this file',
  },
  customPaperwork: {
    title: 'Paperwork – additional questions',
  },
  finalize: {
    htmlTitle: 'Paperwork – finalize',
    title: 'Review and submit',
    subtitle:
      'Review and confirm all the details below. If everything looks good, we’ll send to the provider.',
    submit: 'Submit paperwork',
    prefixes: {
      patient: 'Patient',
      emergencyContact: 'Emergency contact',
    },
    photoId: {
      onFile: 'On file',
      none: 'None',
    },
  },
  start: {
    loading: 'Loading your paperwork',
    edit: {
      cta: 'Edit paperwork',
      subtitleGeneric: 'Thanks for completing paperwork',
      subtitlePersonal: 'Thanks for completing {{name}}’s paperwork',
      makeChanges: 'You can make any needed changes to your paperwork here.',
    },
    begin: {
      titlePersonal: 'Complete {{name}}’s paperwork',
      titleGeneric: 'Complete paperwork',
      subtitle: 'We need a few details to get you the proper care.',
      loginToGoFaster: "If you've used Solv before, log in to make this part go even faster.",
      cta: 'Start paperwork',
      login: 'Log in with mobile number',
      dontLogin: 'Continue as new patient',
    },
  },
  standardFields: {
    patientFirstName: {
      default: 'First name',
      error: 'First name',
      finalize: 'First name',
    },
    patientMiddleName: {
      default: 'Middle name',
      error: 'Middle name',
      finalize: 'Middle name',
    },
    patientLastName: {
      default: 'Last name',
      error: 'Last name',
      finalize: 'Last name',
    },
    birthDate: {
      default: 'Birth date',
      error: 'Birth date',
      finalize: 'Birth date',
    },
    patientGender: {
      default: 'Gender',
      error: 'Gender',
      finalize: 'Gender',
    },
    ethnicity: {
      default: 'Ethnicity',
      error: 'Ethnicity',
      finalize: 'Ethnicity',
    },
    race: {
      default: 'Race',
      error: 'Race',
      finalize: 'Race',
    },
    patientAddressStreet: {
      default: 'Street address',
      error: 'Street address',
      finalize: 'Street address',
    },
    patientAddressStreetTwo: {
      default: 'Address line 2',
      error: 'Address line 2',
      finalize: 'Address line 2',
      helperText: 'Apt#, bld#, suite, etc',
    },
    patientAddressCity: {
      default: 'City',
      error: 'City',
      finalize: 'City',
    },
    patientAddressState: {
      default: 'State',
      error: 'State',
      finalize: 'State',
    },
    patientAddressZip: {
      default: 'Zip code',
      error: 'Zip code',
      finalize: 'Zip code',
    },
    primaryPhone: {
      default: 'Primary phone',
      error: 'Primary phone',
      finalize: 'Primary phone',
    },
    email: {
      default: 'Email',
      error: 'Email',
      finalize: 'Email',
    },
    reasonForVisit: {
      default: 'Reason for visit',
      error: 'Reason for visit',
      finalize: 'Reason for visit',
    },
    physician: {
      default: 'Primary care physician',
      error: 'Primary care physician',
      finalize: 'Primary care physician',
    },
    pharmacy: {
      default: 'Preferred pharmacy',
      error: 'Preferred pharmacy',
      finalize: 'Preferred pharmacy',
    },
    howHearAboutUs: {
      default: 'How did you hear about us?',
      error: 'How did you hear about us?',
      finalize: 'How did you hear about us?',
    },
    workRelatedInjuryCheck: {
      default: 'Is this a work related injury?',
      error: 'Is this a work related injury?',
      finalize: 'Is this a work related injury?',
    },
    employer: {
      default: 'Employer',
      error: 'Employer',
      finalize: 'Employer',
    },
    selfPayCheck: {
      default: 'Pay with insurance?',
      error: 'Pay with insurance?',
      finalize: 'Pay with insurance?',
    },
    insuranceCompany: {
      default: 'Insurance company',
      error: 'Insurance company',
      finalize: 'Insurance company',
    },
    memberId: {
      default: 'Member ID',
      error: 'Member ID',
      finalize: 'Member ID',
    },
    groupNumber: {
      default: 'Group number',
      error: 'Group number',
      finalize: 'Group number',
    },
    policyHolderFirstName: {
      default: 'Policyholder first name',
      error: 'Policyholder first name',
      finalize: 'Policyholder first name',
    },
    policyHolderMiddleName: {
      default: 'Policyholder middle name',
      error: 'Policyholder middle name',
      finalize: 'Policyholder middle name',
    },
    policyHolderLastName: {
      default: 'Policyholder last name',
      error: 'Policyholder last name',
      finalize: 'Policyholder last name',
    },
    policyHolderBirthDate: {
      default: 'Policyholder birth date',
      error: 'Policyholder birth date',
      finalize: 'Policyholder birth date',
    },
    guardianSameAsSubscriberCheck: {
      default: 'Same as subscriber?',
      error: 'Same as subscriber?',
      finalize: 'Same as subscriber?',
    },
    guardianFirstName: {
      default: 'First name',
      error: 'First name',
      finalize: 'Guardian first name',
    },
    guardianMiddleName: {
      default: 'Middle name',
      error: 'Middle name',
      finalize: 'Guardian middle name',
    },
    guardianLastName: {
      default: 'Last name',
      error: 'Last name',
      finalize: 'Guardian last name',
    },
    guardianBirthDate: {
      default: 'Birth date',
      error: 'Birth date',
      finalize: 'Guardian birth date',
    },
    relationshipToPatient: {
      default: 'Relationship to account',
      error: 'Relationship to account',
      finalize: 'Guardian relationship to account',
    },
    guardianAddress: {
      default: 'Guardian street address',
      error: 'Guardian street address',
      finalize: 'Guardian street address',
    },
    guardianAddressTwo: {
      default: 'Guardian street address line 2',
      error: 'Guardian street address line 2',
      finalize: 'Guardian street address line 2',
      helperText: 'Apt#, bld#, suite, etc',
    },
    guardianCity: {
      default: 'Guardian city',
      error: 'Guardian city',
      finalize: 'Guardian city',
    },
    guardianState: {
      default: 'Guardian state',
      error: 'Guardian state',
      finalize: 'Guardian state',
    },
    guardianZip: {
      default: 'Guardian zip code',
      error: 'Guardian zip code',
      finalize: 'Guardian zip code',
    },
    guardianPhone: {
      default: 'Guardian phone',
      error: 'Guardian phone',
      finalize: 'Guardian phone',
    },
    altGuardianPhone: {
      default: 'Guardian alternate phone',
      error: 'Guardian alternate phone',
      finalize: 'Guardian alternate phone',
    },
    guardianEmail: {
      default: 'Guardian email address',
      error: 'Guardian email address',
      finalize: 'Guardian email address',
    },
    emergencyContactSameAsGuardianCheck: {
      default: 'Same as guardian?',
      error: 'Same as guardian?',
      finalize: 'Same as guardian?',
    },
    emergencyContactFirstName: {
      default: 'First name',
      error: 'First name',
      finalize: 'Emergency contact first name',
    },
    emergencyContactMiddleName: {
      default: 'Middle name',
      error: 'Middle name',
      finalize: 'Emergency contact middle name',
    },
    emergencyContactLastName: {
      default: 'Last name',
      error: 'Last name',
      finalize: 'Emergency contact last name',
    },
    emergencyContactPhone: {
      default: 'Phone',
      error: 'Phone',
      finalize: 'Emergency contact phone',
    },
    emergencyContactRelationshipToPatient: {
      default: 'Relationship to patient',
      error: 'Relationship to patient',
      finalize: 'Emergency contact relationship to patient',
    },
    workInjuryEmployerContactName: {
      default: 'Employer contact name',
      error: 'Employer contact name',
      finalize: 'Employer contact name',
    },
    workInjuryEmployerContactPhoneNumber: {
      default: 'Employer contact phone number',
      error: 'Employer contact phone number',
      finalize: 'Employer contact phone number',
    },
    workInjuryDateOfInjury: {
      default: 'Date of injury',
      error: 'Date of injury',
      finalize: 'Date of injury',
    },
    ssn: {
      default: 'Social security number',
      error: 'Social security number',
      finalize: 'Social security number',
    },
    maritalStatus: {
      default: 'Marital status',
      error: 'Marital status',
      finalize: 'Marital status',
    },
    areYouBeingTreatedAtTheRequestOfYourEmployer: {
      default: 'Are you here at the request of your employer?',
      error: 'Are you here at the request of your employer?',
      finalize: 'Are you here at the request of your employer?',
    },
    whatAreYouHereFor: {
      default: 'What are you here for?',
      error: 'What are you here for?',
      finalize: 'What are you here for?',
    },
    whatTypeOfPhysical: {
      default: 'What type of physical?',
      error: 'What type of physical?',
      finalize: 'What type of physical?',
    },
    whatTypeOfDrugScreenAlcoholTest: {
      default: 'What type of Drug Screen and/or Alcohol Test?',
      error: 'What type of Drug Screen and/or Alcohol Test?',
      finalize: 'What type of Drug Screen and/or Alcohol Test?',
    },
    howDidYourInjuryOccur: {
      default: 'How did your injury occur?',
      error: 'How did your injury occur?',
      finalize: 'How did your injury occur?',
    },
    whenDidYourInjuryOccur: {
      default: 'When did your injury occur?',
      error: 'When did your injury occur?',
      finalize: 'When did your injury occur?',
    },
    toWhomReportedYourInjury: {
      default: 'To whom did you report your injury, if anyone?',
      error: 'To whom did you report your injury, if anyone?',
      finalize: 'To whom did you report your injury, if anyone?',
    },
    whereWasPriorTreatment: {
      default: 'If you received prior treatment, where was it?',
      error: 'If you received prior treatment, where was it?',
      finalize: 'If you received prior treatment, where was it?',
    },
    doYouHaveSecondInsuranceInformation: {
      default: 'Do you have secondary insurance information you would like to add?',
      error: 'Do you have secondary insurance information you would like to add?',
      finalize: 'Do you have secondary insurance information you would like to add?',
    },
    doYouHaveSecondInsuranceInformationMobile: {
      default: 'Do you have secondary insurance?',
      error: 'Do you have secondary insurance?',
      finalize: 'Do you have secondary insurance?',
    },
    secondaryInsuranceCompany: {
      default: 'Insurance company',
      error: 'Insurance company',
      finalize: 'Insurance company',
    },
    secondaryMemberId: {
      default: 'Member ID',
      error: 'Member ID',
      finalize: 'Member ID',
    },
    secondaryGroupNumber: {
      default: 'Group number',
      error: 'Group number',
      finalize: 'Group number',
    },
    secondaryPolicyHolderFirstName: {
      default: 'Policyholder first name',
      error: 'Policyholder first name',
      finalize: 'Policyholder first name',
    },
    secondaryPolicyHolderMiddleName: {
      default: 'Policyholder middle name',
      error: 'Policyholder middle name',
      finalize: 'Policyholder middle name',
    },
    secondaryPolicyHolderLastName: {
      default: 'Policyholder last name',
      error: 'Policyholder last name',
      finalize: 'Policyholder last name',
    },
    secondaryPolicyHolderBirthDate: {
      default: 'Policyholder birth date',
      error: 'Policyholder birth date',
      finalize: 'Policyholder birth date',
    },
    customEmployer: {
      default: 'Employer',
      error: 'Employer',
      finalize: 'Employer',
    },
    patientBirthSex: {
      default: 'Patient sex',
      error: 'Patient sex',
      finalize: 'Patient sex',
    },
    policyHolderBirthSex: {
      default: 'Policyholder birth sex',
      error: 'Policyholder birth sex',
      finalize: 'Policyholder birth sex',
    },
    patientsRelationshipToInsured: {
      default: 'Patient’s relationship to insured',
      error: 'Patient’s relationship to insured',
      finalize: 'Patient’s relationship to insured',
    },
    secondaryPatientsRelationshipToInsured: {
      default: 'Patient’s relationship to insured',
      error: 'Patient’s relationship to insured',
      finalize: 'Patient’s relationship to insured',
    },
    legalTreatment: {
      default: 'Legal {{field}}',
      error: 'Legal {{field}}',
      finalize: 'Legal {{field}}',
    },
    insurancePlanName: {
      default: 'Insurance plan',
      error: 'Insurance plan',
      finalize: 'Insurance plan',
    },
    frontOfId: {
      default: 'Front of ID',
      error: 'Front of ID',
      finalize: 'Front of ID',
    },
    backOfId: {
      default: 'Back of ID',
      error: 'Back of ID',
      finalize: 'Back of ID',
    },
  },
  responses: {
    '': '',
    Male: 'Male',
    Female: 'Female',
    Other: 'Other',
    'Transgender Woman/Transgender Female': 'Transgender woman/transgender female',
    'Transgender Man/Transgender Male': 'Transgender man/transgender male',
    'Chose not to disclose': 'Chose not to disclose',
    Unknown: 'Unknown',
    'Hispanic/Latino': 'Hispanic/Latino',
    'Not Hispanic/Latino': 'Not Hispanic/Latino',
    'Prefer not to answer': 'Prefer not to answer',
    'American Indian or Alaska Native': 'American Indian or Alaska Native',
    Asian: 'Asian',
    'Black or African American': 'Black or African American',
    'Hawaiian or Pacific Islander': 'Hawaiian or Pacific Islander',
    White: 'White',
    Solv: 'Solv',
    'Community Event': 'Community event',
    'School Event/Promotion': 'School event/promotion',
    Pediatrician: 'Pediatrician',
    'Flu Shot Clinic': 'Flu shot clinic',
    'Billboard/Mailer': 'Billboard/mailer',
    'Google/Internet': 'Google/internet',
    Facebook: 'Facebook',
    'Shopping Center': 'Shopping center',
    Friend: 'Friend',
    'Other/Return': 'Other/return',
    Yes: 'Yes',
    No: 'No',
    Single: 'Single',
    Married: 'Married',
    Divorced: 'Divorced',
    Widowed: 'Widowed',
    Separated: 'Separated',
    Physical: 'Physical',
    'Drug Screen and/or Alcohol Test': 'Drug screen and/or alcohol test',
    'Work Injury': 'Work injury',
    DOT: 'DOT',
    'Pre-Employment': 'Pre-employment',
    Wellness: 'Wellness',
    'Fit for Duty': 'Fit for duty',
    other: 'other',
    'Post-Accident': 'Post-accident',
    Random: 'Random',
    'Reasonable Suspicion': 'Reasonable suspicion',
    Sibling: 'Sibling',
    Child: 'Child',
    Employee: 'Employee',
    Father: 'Father',
    Grandchild: 'Grandchild',
    Grandparent: 'Grandparent',
    Mother: 'Mother',
    'Other Relationship': 'Other relationship',
    Self: 'Self',
    Spouse: 'Spouse',
  },
  photoId: {
    title: 'Add photos of the patient’s ID',
    subtitle:
      'The provider needs to verify the patient’s identity with photos of their government-issued ID.',
    guardian: {
      title: 'Add photos of the guardian’s ID',
      subtitle:
        'The provider needs to verify the guardian’s identity with photos of their government-issued ID.',
    },
    back: {
      title: 'Now, let’s do the back of the ID',
    },
    howDoesItLook: 'How does it look?',
    instructions: {
      title: 'When taking a photo, make sure the ID...',
      isFlat: 'Is lying flat and centered in the camera view.',
      wellLit: 'Is well lit with minimal glare on the surface.',
    },
    ctas: {
      addFrontPhoto: 'Add front photo',
      addBackPhoto: 'Add back photo',
      looksGood: 'Looks good',
      retakeFront: 'Retake front photo',
      retakeBack: 'Retake back photo',
    },
    dragAndDrop: 'or drag and drop file',
  },
};
